import { Autocomplete, Grid, Paper, Skeleton, TextField, Tooltip, Typography } from '@mui/material';
import InputForm from '../../commun/input-form';
import React from 'react';
import { ReferenceDto } from '../../../services/dtos/reference.dto';
import './move-house.form.scss';
import InfoIcon from '@mui/icons-material/Info';
import imgMeterReading from '../../../assets/images/releveCompteur.png';
import theme from '../../../theme';
import { AddressGouvFeaturesPropertiesFormDto } from '../../../services/dtos/form/address-gouv-features-properties-form.dto';
import { parseHouseNumber } from '../../../utils/address-parser.utils';
import { useFormikContext } from 'formik';

/**
 * Partie du formulaire de déménagement : infos départ de la personne
 * @param props
 * @constructor
 */
export default function MoveHouseInfosForm(props: {
    handleChangeCountry: (e: React.SyntheticEvent, v: ReferenceDto | null) => void;
    countriesSelection: ReferenceDto[];
    selectedCountry?: ReferenceDto;
    handleSearchAddress?: (event: object, value: string, reason: string) => void;
    addressesGouv?: AddressGouvFeaturesPropertiesFormDto[];
    handleSelectAddress?: (address: AddressGouvFeaturesPropertiesFormDto | null) => void;
}) {
    const { setFieldValue } = useFormikContext();
    return (
        <>
            {/*Date de sortie*/}
            <Grid item xs={12} md={6}>
                {/*1er encadré avec votre demande*/}
                <Paper>
                    <Typography variant="h5">Votre Demande :</Typography>
                    <InputForm
                        type="date"
                        id="dateMoving"
                        name="dateMoving"
                        label="Date de sortie du logement actuel"
                        required
                    />
                </Paper>
            </Grid>
            {/*relevé de compteur*/}
            <Grid item xs={12} md={6} sx={{ height: '100%' }}>
                {/*2eme encadré avec relevé de compteur*/}
                <Paper>
                    <Typography variant="h5">Relevé de compteur :</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={11}>
                            <InputForm
                                type="number"
                                id="meterReading"
                                name="meterReading"
                                label="Relevé de votre compteur actuel"
                                required
                            />
                        </Grid>
                        {/*Ajout d'un Tooltip avec une image*/}
                        <Grid item xs={1}>
                            <Tooltip
                                title={
                                    <img
                                        className="move-house-tooltip-img"
                                        src={imgMeterReading}
                                        alt="Relevè de compteur"
                                    />
                                }
                                className="move-house-tooltip"
                                sx={{ color: theme.palette.blue.main }}
                            >
                                <InfoIcon />
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            {/*Adresse*/}
            <Grid item xs={12}>
                <Paper>
                    <Typography variant="h5">Nouvelle adresse de résidence</Typography>

                    {/* Ajout rapide d'adresse via API data.gouv */}
                    {props.handleSearchAddress && props.addressesGouv && (
                        <Autocomplete
                            data-testid="autocomplete-address-demenagement"
                            disablePortal
                            id="quickAddress"
                            autoHighlight
                            options={props.addressesGouv}
                            getOptionLabel={option => option.label}
                            onInputChange={props.handleSearchAddress}
                            onChange={(_, value) => {
                                if (value && setFieldValue) {
                                    // Parser le numéro de rue pour séparer le numéro des bis/ter
                                    const parsedNumber = parseHouseNumber(value.housenumber || '');

                                    // Remplir automatiquement les champs du formulaire
                                    setFieldValue('numeroRue', parsedNumber.numero);
                                    setFieldValue('rue', value.street || value.name || '');
                                    setFieldValue('ville', value.city || '');
                                    setFieldValue('codePostal', value.postcode || '');
                                }
                                if (props.handleSelectAddress) {
                                    props.handleSelectAddress(value);
                                }
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Rechercher votre nouvelle adresse (optionnel)"
                                    helperText="Tapez votre adresse pour remplir automatiquement les champs ci-dessous"
                                    sx={{ marginTop: '20px', marginBottom: '20px' }}
                                />
                            )}
                        />
                    )}

                    {/*Début grid*/}
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={2}>
                            {/*Champ numero de rue*/}
                            <InputForm id="numeroRue" name="numeroRue" required label={'N°'}></InputForm>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            {/*Champ complement numero de rue*/}
                            <InputForm
                                id="complementnumeroRue"
                                name="complement.numeroRue"
                                label={'Bis, Ter, ...'}
                            ></InputForm>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            {/*Champ nom de rue*/}
                            <InputForm id="rue" name="rue" required label={'Rue'}></InputForm>
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            {/*Champ complément de rue*/}
                            <InputForm id="complementRue" name="complement.rue" label={'Complément de rue'}></InputForm>
                        </Grid>
                        {/* Champs compléments d'adresse */}
                        <Grid item xs={12} sm={6}>
                            <InputForm
                                id="complement.batiment"
                                name="complement.batiment"
                                label={'Entrée, résidence...'}
                            ></InputForm>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputForm
                                id="complement.escalier"
                                name="complement.escalier"
                                label={'Escalier'}
                            ></InputForm>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputForm id="complement.etage" name="complement.etage" label={'Étage'}></InputForm>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputForm
                                id="complement.appartement"
                                name="complement.appartement"
                                label={'Appartement'}
                            ></InputForm>
                        </Grid>
                        {/*Champ code postal*/}
                        <Grid item xs={12} sm={4}>
                            <InputForm id="codePostal" name="codePostal" required label={'Code postal'}></InputForm>
                        </Grid>
                        {/*Champ ville*/}
                        <Grid item xs={12} sm={4}>
                            <InputForm id="ville" name="ville" required label={'Ville'}></InputForm>
                        </Grid>
                        {/*Champ complement ville*/}
                        <Grid item xs={12} sm={4}>
                            <InputForm
                                id="complementville"
                                name="complement.ville"
                                label={'Complément ville'}
                            ></InputForm>
                        </Grid>
                        {/*Champ boîte postale*/}
                        <Grid item xs={12} sm={4}>
                            <InputForm id="boitePostale" name="boitePostale" label={'Boîte postale'}></InputForm>
                        </Grid>
                        {/*Champ pays*/}
                        {props.countriesSelection ? (
                            <Grid item xs={12} sm={4}>
                                <Autocomplete
                                    data-testid="autocomplete-pays"
                                    disablePortal
                                    id="pays"
                                    autoHighlight
                                    options={props.countriesSelection}
                                    value={props.selectedCountry}
                                    onChange={props.handleChangeCountry}
                                    getOptionLabel={option => option.label}
                                    renderInput={params => <TextField {...params} name="pays" label="Pays" required />}
                                ></Autocomplete>
                            </Grid>
                        ) : (
                            <Skeleton variant="rectangular" height={100}></Skeleton>
                        )}
                    </Grid>
                </Paper>
            </Grid>
        </>
    );
}
